import { motion } from "framer-motion";
import { Check, Star } from "@phosphor-icons/react";
import { useLang } from "@/context/LanguageContext";

const meta = [
  { id: "plan_mini", name: "Plan Mini", original: 19.99, price: 13.99, accent: "hsl(200 75% 82%)", featured: false },
  { id: "plan_pro", name: "Plan Pro", original: 24.99, price: 17.49, accent: "hsl(351 100% 82%)", featured: true },
];

export default function Pricing({ onSelect }) {
  const { t } = useLang();
  const plans = meta.map((m) => ({
    ...m,
    features: t(`pricing.plans.${m.id}`),
    badge: m.featured ? t("pricing.popular") : null,
  }));
  return (
    <section id="paquetes" className="relative py-24 max-w-7xl mx-auto px-5 sm:px-8">
      <div className="text-center max-w-2xl mx-auto mb-16">
        <p className="font-hand text-3xl text-accent mb-2">{t("pricing.kicker")}</p>
        <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-foreground">
          {t("pricing.title")}
        </h2>
        <p className="mt-4 text-foreground/60 font-medium">{t("pricing.subtitle")}</p>
        <motion.div
          data-testid="pricing-offer-banner"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary/15 border border-primary/30 px-5 py-2"
        >
          <Star weight="fill" size={16} className="text-primary" />
          <span className="text-sm font-bold text-primary">{t("pricing.save")}</span>
        </motion.div>
      </div>

      <div className="grid sm:grid-cols-2 gap-6 lg:gap-8 items-center max-w-3xl mx-auto">
        {plans.map((p, i) => {
          const featured = !!p.badge;
          return (
            <motion.div
              key={p.id}
              data-testid={`plan-card-${p.id}`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 130, damping: 18, delay: i * 0.1 }}
              whileHover={{ y: -10 }}
              className={`relative rounded-[2.5rem] p-8 border ${
                featured
                  ? "bg-foreground text-background shadow-soft-lg md:scale-105 border-transparent z-10"
                  : "bg-white text-foreground shadow-glass border-white"
              }`}
            >
              <div
                data-testid={`plan-discount-${p.id}`}
                className="absolute -top-4 right-6 flex items-center gap-1.5 rounded-full bg-accent px-4 py-1.5 text-xs font-black text-foreground shadow-soft"
              >
                {t("pricing.off")}
              </div>
              {p.badge && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-xs font-bold text-primary-foreground shadow-soft">
                  <Star weight="fill" size={14} /> {p.badge}
                </div>
              )}
              <div className="w-14 h-14 rounded-2xl mb-6" style={{ background: p.accent }} />
              <h3 className="font-display font-semibold text-2xl mb-1">{p.name}</h3>
              <div className="mb-6">
                <div className={`flex items-center gap-2 text-sm font-bold ${featured ? "text-background/50" : "text-foreground/40"}`}>
                  <span>{t("pricing.before")}</span>
                  <span className="line-through" data-testid={`plan-original-${p.id}`}>${p.original}</span>
                </div>
                <div className="flex items-end gap-1">
                  <span className="font-display font-bold text-6xl tracking-tight" data-testid={`plan-price-${p.id}`}>${p.price}</span>
                  <span className={`mb-2 text-sm font-semibold ${featured ? "text-background/60" : "text-foreground/50"}`}>USD</span>
                </div>
              </div>
              <ul className="space-y-3 mb-8">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm font-medium">
                    <span
                      className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
                      style={{ background: featured ? "hsl(351 100% 78%)" : p.accent }}
                    >
                      <Check weight="bold" size={12} className="text-foreground" />
                    </span>
                    <span className={featured ? "text-background/90" : "text-foreground/70"}>{f}</span>
                  </li>
                ))}
              </ul>
              <motion.button
                data-testid={`plan-select-${p.id}`}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                onClick={() => onSelect(p.id)}
                className={`w-full rounded-full py-3.5 font-bold shadow-soft ${
                  featured ? "bg-primary text-primary-foreground" : "bg-foreground text-background"
                }`}
              >
                {t("pricing.choose")} {p.name}
              </motion.button>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
